import type { ColumnDef, Row } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';
import { Fragment } from 'react';
import { cn } from '@/shared/lib/utils';
import type { ExpandableConfig, PinnedColumnInfo } from '../helpers/types';
import {
  getHighlightedPinnedClassName,
  getPinnedClassName,
  mergeColumnStyles,
} from '../helpers/utils';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';

type TableDataRowsProps<TData, TValue> = {
  rows: Row<TData>[];
  columns: ColumnDef<TData, TValue>[];
  columnStyles: Record<string, CSSProperties | undefined>;
  weightedLeafWidthStyles: Record<string, CSSProperties>;
  pinnedColumnStyles?: Record<string, PinnedColumnInfo>;
  showLastBorderBottom: boolean;
  expandable?: ExpandableConfig<TData>;
  highlightedRowIds?: string[];
  selectedRowIds?: string[];
};

/**
 * テーブルのデータ行をレンダリングするコンポーネント
 */
export const TableDataRows = <TData, TValue>({
  rows,
  columns,
  columnStyles,
  weightedLeafWidthStyles,
  pinnedColumnStyles,
  showLastBorderBottom,
  expandable,
  highlightedRowIds,
  selectedRowIds,
}: TableDataRowsProps<TData, TValue>) => {
  return (
    <>
      {rows.map((row, index) => {
        const isLastRow = index === rows.length - 1;
        const canExpand = expandable?.rowExpandable?.(row) ?? row.getCanExpand();
        const isExpanded = row.getIsExpanded();
        const expandedContent: ReactNode =
          canExpand && isExpanded && expandable?.expandedRowRender
            ? expandable.expandedRowRender(row)
            : null;
        const isHighlighted = highlightedRowIds?.includes(row.id);
        const isSelected = selectedRowIds?.includes(row.id);
        const visibleCells = row.getVisibleCells();

        return (
          <Fragment key={row.id}>
            <TableRow
              data-row-id={row.id}
              data-state={(row.getIsSelected() || isSelected) && 'selected'}
              className={cn(
                isLastRow && !showLastBorderBottom && 'border-b-0',
                isSelected && 'bg-surface-accent hover:bg-surface-accent',
                isHighlighted && 'relative shadow-[inset_0_0_0_2px_#059669]',
              )}
            >
              {visibleCells.map((cell, cellIndex) => {
                const style = columnStyles[cell.column.id];
                const weightedStyle = weightedLeafWidthStyles[cell.column.id];
                const mergedStyle = mergeColumnStyles(style, weightedStyle);
                const pinned = pinnedColumnStyles?.[cell.column.id];
                const finalStyle = pinned ? { ...mergedStyle, ...pinned.style } : mergedStyle;
                const pinnedDataAttrs = pinned
                  ? pinned.side === 'left'
                    ? { 'data-pinned-left': pinned.pinnedOffset }
                    : { 'data-pinned-right': pinned.pinnedOffset }
                  : {};
                return (
                  <TableCell
                    key={cell.id}
                    style={finalStyle}
                    className={cn(
                      getPinnedClassName({ pinned, isCell: true }),
                      isHighlighted &&
                        pinned &&
                        getHighlightedPinnedClassName({
                          isFirstCell: cellIndex === 0,
                          isLastCell: cellIndex === visibleCells.length - 1,
                        }),
                    )}
                    {...pinnedDataAttrs}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
            {expandedContent && (
              <TableRow key={`${row.id}-expanded`}>
                <TableCell colSpan={columns.length} className="p-0">
                  {expandedContent}
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        );
      })}
    </>
  );
};
